import { css } from 'lit';

export default css`
  :host {
    display: block;
    /* Allow the host to shrink inside flex parents (e.g. a table cell) so the
       scroll region is bounded by the cell rather than forcing it wider. */
    min-width: 0;

    /* Public custom properties */
    --fade-size: 1.5rem; /* length of the fade cue at each scrollable edge */
    --max-block-size: 12rem; /* height cap that creates the vertical scroll region */
  }

  .fade {
    /* Internal fade extents, flipped on per edge by the component when there is
       more content that way. 0px means "no fade on this edge". */
    --_start: 0px;
    --_end: 0px;

    max-width: 100%;

    -webkit-mask-image: var(--_fade-gradient);
    mask-image: var(--_fade-gradient);
  }

  /* Horizontal (default): keep content on one line and scroll sideways. */
  :host(:not([orientation='vertical'])) .fade {
    --_fade-gradient: linear-gradient(
      to right,
      transparent 0,
      #000 var(--_start),
      #000 calc(100% - var(--_end)),
      transparent 100%
    );

    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
  }

  /* Vertical: let content wrap, cap the height and scroll down. */
  :host([orientation='vertical']) .fade {
    --_fade-gradient: linear-gradient(
      to bottom,
      transparent 0,
      #000 var(--_start),
      #000 calc(100% - var(--_end)),
      transparent 100%
    );

    overflow-x: hidden;
    overflow-y: auto;
    max-block-size: var(--max-block-size);
  }

  /* Edge fades: only present while there is more content beyond that edge. */
  .fade.is-start {
    --_start: var(--fade-size);
  }

  .fade.is-end {
    --_end: var(--fade-size);
  }

  /* without-shadow removes the fade cue but keeps the region scrollable. */
  :host([without-shadow]) .fade {
    -webkit-mask-image: none;
    mask-image: none;
  }

  /* without-scrollbar hides the bar but keeps scrolling. This is the
     accessibility trade-off the consumer opts into: the region stays
     keyboard-scrollable (a tabindex is added while it overflows), but the
     visible affordance is gone. */
  :host([without-scrollbar]) .fade {
    scrollbar-width: none;
  }

  :host([without-scrollbar]) .fade::-webkit-scrollbar {
    display: none;
  }

  /* With the bar hidden the region is drag-scrollable by mouse; hint it with a
     grab cursor while it can actually scroll. */
  :host([without-scrollbar]) .fade.is-scrollable {
    cursor: grab;
  }

  :host([without-scrollbar]) .fade.is-dragging {
    cursor: grabbing;
    user-select: none;
  }

  /* Focus ring for the keyboard-scrollable region. */
  .fade:focus-visible {
    outline: var(--webmodule-focus-border);
    outline-offset: var(--webmodule-focus-border-offset);
  }
`;
